import React, { useState, useCallback } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  ViewColumn as ViewColumnIcon,
  Check as CheckIcon,
  PersonAddAlt as PersonAddAltIcon,
} from '@mui/icons-material';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

export type InboxCrmStageMenuProps = {
  contactId?: string | null;
  /** Atualizar badges de estágio (ex.: checkAllLeadsStages no inbox) */
  onStageChanged?: () => void;
  sx?: object;
  size?: 'small' | 'medium';
};

type StageRow = {
  id: string;
  name: string;
  stage_order: number | string;
  color?: string | null;
};

type PipelineRow = { id: string; name: string; stages: StageRow[] };

function sortStages(stages: StageRow[]): StageRow[] {
  return [...stages].sort((a, b) => {
    const ao = Number(a.stage_order);
    const bo = Number(b.stage_order);
    if (!Number.isNaN(ao) && !Number.isNaN(bo)) return ao - bo;
    return String(a.stage_order).localeCompare(String(b.stage_order));
  });
}

function findPipelineForStage(pipelines: PipelineRow[], stageId: string | null | undefined): PipelineRow | null {
  if (!stageId) return null;
  for (const p of pipelines) {
    if (p.stages?.some((s) => s.id === stageId)) return p;
  }
  return null;
}

function defaultPipeline(pipelines: PipelineRow[]): PipelineRow | null {
  const byName = pipelines.find((p) => p.name === 'Funil de Vendas');
  return byName || pipelines[0] || null;
}

const InboxCrmStageMenu: React.FC<InboxCrmStageMenuProps> = ({
  contactId,
  onStageChanged,
  sx,
  size = 'small',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [pipelines, setPipelines] = useState<PipelineRow[]>([]);
  const [currentStageId, setCurrentStageId] = useState<string | null>(null);
  const [inFunnel, setInFunnel] = useState(false);

  const open = Boolean(anchorEl);
  const api = getApiUrl();

  const loadMenuData = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const [pipRes, leadRes] = await Promise.all([
        axios.get(`${api}/crm/pipelines`),
        axios.get(`${api}/crm/leads/${contactId}`),
      ]);

      const rawPipelines: PipelineRow[] = (pipRes.data?.data || []).map((p: PipelineRow) => ({
        ...p,
        stages: sortStages(p.stages || []),
      }));
      setPipelines(rawPipelines);

      const lead = leadRes.data?.data;
      const sid = lead?.stageId as string | undefined;
      if (sid) {
        setCurrentStageId(sid);
        setInFunnel(true);
      } else {
        setCurrentStageId(null);
        setInFunnel(false);
      }
    } catch {
      setPipelines([]);
      setCurrentStageId(null);
      setInFunnel(false);
    } finally {
      setLoading(false);
    }
  }, [api, contactId]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    void loadMenuData();
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActionLoading(null);
  };

  const handleMoveToStage = async (stageId: string) => {
    if (!contactId || !stageId || actionLoading) return;
    setActionLoading(stageId);
    try {
      const res = await axios.post(`${api}/crm/contact/${contactId}/move-to-stage`, { stageId });
      if (res.data?.success) {
        setCurrentStageId(stageId);
        setInFunnel(true);
        onStageChanged?.();
        handleClose();
      } else {
        alert(res.data?.error || 'Não foi possível mover o lead.');
      }
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.message || 'Erro ao mover estágio.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddToPipeline = async (stageId: string) => {
    if (!contactId || !stageId || actionLoading) return;
    setActionLoading(`add-${stageId}`);
    try {
      const res = await axios.post(`${api}/crm/contact/${contactId}/add-to-pipeline`, {
        source: 'whatsapp',
        stageId,
      });
      if (res.data?.success) {
        setCurrentStageId(stageId);
        setInFunnel(true);
        onStageChanged?.();
        handleClose();
      } else {
        alert(res.data?.error || 'Não foi possível adicionar ao funil.');
      }
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.message || 'Erro ao adicionar ao funil.');
    } finally {
      setActionLoading(null);
    }
  };

  if (!contactId) return null;

  const primaryPipeline = inFunnel
    ? findPipelineForStage(pipelines, currentStageId) || defaultPipeline(pipelines)
    : defaultPipeline(pipelines);

  const otherPipelines = pipelines.filter((p) => p.id !== primaryPipeline?.id);

  return (
    <>
      <Tooltip title="Estágio no CRM">
        <span>
          <IconButton size={size} onClick={handleOpen} sx={sx} aria-label="Mover estágio do CRM">
            <ViewColumnIcon sx={{ fontSize: size === 'small' ? 20 : 24 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ px: 2, py: 1, maxWidth: 320 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {inFunnel ? 'Mover para estágio' : 'Colocar no funil'}
          </Typography>
          {primaryPipeline && (
            <Typography variant="caption" display="block" color="text.secondary">
              {primaryPipeline.name}
            </Typography>
          )}
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !primaryPipeline || !primaryPipeline.stages?.length ? (
          <MenuItem disabled>
            <ListItemText primary="Nenhum funil configurado" />
          </MenuItem>
        ) : (
          <>
            {primaryPipeline.stages.map((st) => {
              const isCurrent = inFunnel && st.id === currentStageId;
              const busy = actionLoading === st.id || actionLoading === `add-${st.id}`;
              return (
                <MenuItem
                  key={st.id}
                  disabled={busy || (inFunnel && isCurrent)}
                  onClick={() => (inFunnel ? handleMoveToStage(st.id) : handleAddToPipeline(st.id))}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {busy ? (
                      <CircularProgress size={18} />
                    ) : isCurrent ? (
                      <CheckIcon fontSize="small" color="primary" />
                    ) : inFunnel ? null : (
                      <PersonAddAltIcon fontSize="small" color="action" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={st.name}
                    secondary={inFunnel && isCurrent ? 'Estágio atual' : undefined}
                  />
                </MenuItem>
              );
            })}
            {inFunnel && otherPipelines.length > 0 && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ px: 2, py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Outros funis
                  </Typography>
                </Box>
                {otherPipelines.map((pipe) =>
                  sortStages(pipe.stages || []).map((st) => {
                    const busy = actionLoading === st.id;
                    return (
                      <MenuItem key={`${pipe.id}-${st.id}`} disabled={busy} onClick={() => handleMoveToStage(st.id)}>
                        <ListItemIcon sx={{ minWidth: 36 }}>{busy ? <CircularProgress size={18} /> : null}</ListItemIcon>
                        <ListItemText primary={`${st.name}`} secondary={pipe.name} />
                      </MenuItem>
                    );
                  })
                )}
              </>
            )}
          </>
        )}
      </Menu>
    </>
  );
};

export default InboxCrmStageMenu;
